import moment from 'moment';
import type ApplicationModel from 'components/framework/ApplicationModel';
import Iteration from '../Iteration';

export interface HasIterations {
  /**
   * Load all the iterations available to this team (project).
   * Results are cached per-instance using an internal WeakMap.
   */
  availableIterations(): Promise<Iteration[]>;

  getIteration({
    iterationId,
  }: {
    iterationId?: string;
  }): Promise<Iteration | undefined>;
}

// Per-instance cache: WeakMap(instance -> Map(projectId(string) -> Promise<Iteration[]>))
const __iterationsCache = new WeakMap<
  object,
  Map<string, Promise<Iteration[]>>
>();

type Project = ApplicationModel & { projectId: string | number };

/**
 * Extend a model with iteration-loading behavior.
 */
// Overload 1: broad ClassDecorator compatibility for @HasIterations usage
export function HasIterations(originalClass: {
  new (...args: any[]): Project;
}): any;
// Overload 2: strongly-typed mixin usage
export function HasIterations<TBase extends new (...args: any[]) => Project>(
  Base: TBase
): new (...args: ConstructorParameters<TBase>) => InstanceType<TBase> &
  HasIterations;

// Implementation
export function HasIterations<TBase extends new (...args: any[]) => Project>(
  Base: TBase
) {
  class WithIterations extends Base implements HasIterations {
    /**
     * Load all the iterations available to this team
     * - Race-safe: concurrent calls share the same in-flight Promise
     * - Per-project cache: keyed by projectId
     */
    availableIterations(): Promise<Iteration[]> {
      let byProject = __iterationsCache.get(this);
      if (!byProject) {
        byProject = new Map<string, Promise<Iteration[]>>();
        __iterationsCache.set(this, byProject);
      }

      const key = String(this.projectId);
      const existing = byProject.get(key);
      if (existing) return existing;

      // Create the fetch promise first and store it immediately to avoid races.
      const fetchPromise = Iteration.select(
        'id',
        'name',
        'status',
        'duration',
        'startDate',
        'endDate',
        'createdAt'
      )
        .merge({ originalEstimate: ['units', 'value'] })
        .where({ projectId: this.projectId, status: [20, 30] })
        .order({ startDate: 'DESC' })
        .all()
        .then((iterations: Iteration[]) =>
          iterations.filter(i =>
            moment(i.createdAt).isAfter(Iteration.BURNDOWN_AVAILABLE_DATE)
          )
        );

      // Ensure failures don't poison the cache: delete on rejection so retries work.
      const cached = fetchPromise.catch(err => {
        byProject.delete(key);
        throw err;
      });

      byProject.set(key, cached);
      return cached;
    }

    async getIteration({
      iterationId,
    }: {
      iterationId?: string;
    }): Promise<Iteration | undefined> {
      const iterations = await this.availableIterations();
      if (!iterations?.length) return undefined;

      if (!iterationId) return iterations[0];

      return (
        iterations.find(i => String(i.id) === iterationId) || iterations[0]
      );
    }
  }
  return WithIterations as unknown as new (
    ...args: ConstructorParameters<TBase>
  ) => InstanceType<TBase> & HasIterations;
}
